// latestWins.js — apply the newest server answer that has ARRIVED, instead of
// only the answer to the newest request SENT.
//
// The Lifecycle / Video / Bid Health tabs re-post the whole session to the
// proxy on every row change. They used to drop an in-flight answer as soon as
// the rows changed again (`cancelled = true` in the effect cleanup). During a
// live capture rows arrive every ~2s, and posting + computing a session can
// take longer than that, so EVERY answer was dropped and the tab froze on the
// last one it got until traffic paused. Now an in-flight answer still lands,
// unless a newer one already did: the screen is at most one round trip stale,
// never stuck.

import { useEffect, useRef } from 'react';

export function createLatestWins() {
  let sent = 0;
  let applied = 0;
  return {
    /** Number this request; pass the result to accept() when it answers. */
    begin() { sent += 1; return sent; },
    /** True when this answer is newer than anything shown; marks it shown. */
    accept(seq) {
      if (seq < applied) return false;
      applied = seq;
      return true;
    },
    /** Drop every in-flight answer (the view was reset, e.g. rows cleared). */
    invalidate() { sent += 1; applied = sent; },
  };
}

/**
 * useLatestWins() -> { begin, accept, invalidate }
 * Same object for the component's lifetime; accept() is always false after
 * unmount, so a late answer never sets state on an unmounted view.
 */
export function useLatestWins() {
  const ref = useRef(null);
  const mounted = useRef(true);
  if (!ref.current) ref.current = createLatestWins();
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const lw = ref.current;
  return {
    begin: lw.begin,
    accept: (seq) => mounted.current && lw.accept(seq),
    invalidate: lw.invalidate,
  };
}
